import { MERCHANT_TERMS, TRIP_TERMS } from '@field-support/shared';
import { adminAr } from './admin.ar';
import { analyticsAr } from './analytics.ar';
import { catalogAr } from './catalog.ar';
import { formsAr } from './forms.ar';
import { importAr } from './import.ar';
import { trackingAr } from './tracking.ar';

/**
 * Arabic (default). Seeded with only what the shipped pages need; each page PR
 * adds its own keys. Translations are carried over from the previous app:
 * wording is reviewed content, not code.
 */
export const ar = {
  // New copy (UX audit #12): needs native review.
  dateRange: {
    label: 'الفترة',
    today: 'اليوم',
    last7: 'آخر 7 أيام',
    last14: 'آخر 14 يومًا',
    thisMonth: 'هذا الشهر',
    custom: 'مخصص',
  },
  common: {
    // New copy (UX audit #11): needs native review.
    clearFilters: 'مسح التصفية',
    appName: 'المحرّك',
    language: 'English',
    loading: 'جارٍ التحميل…',
    forbidden: 'هذا الحساب ليس له صلاحية لعرض هذه الصفحة.',
    notFound: 'الصفحة غير موجودة.',
    welcome: 'أهلاً بك. اختر صفحة من القائمة.',
    logout: 'تسجيل الخروج',
    save: 'حفظ',
    saving: 'جارٍ الحفظ…',
    saved: 'تم الحفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    edit: 'تعديل',
    close: 'إغلاق',
    search: 'بحث',
    clearSearch: 'مسح البحث',
    showPassword: 'إظهار كلمة المرور', // new — needs native review
    hidePassword: 'إخفاء كلمة المرور', // new — needs native review
    retry: 'إعادة المحاولة',
    actions: 'إجراءات',
    selectAll: 'تحديد كل الصفوف',
    selectRow: 'تحديد الصف',
    status: 'الحالة',
    name: 'الاسم',
    code: 'الكود',
    previous: 'السابق',
    next: 'التالي',
    page: 'صفحة',
    items: 'عنصر',
    noData: 'لا توجد سجلات.',
    errorTitle: 'تعذر إكمال الطلب',
    active: 'نشط',
    brand: `${MERCHANT_TERMS.ar.the}`,
    branch: 'الفرع',
    primaryNav: 'التنقل الرئيسي',
    agent: 'المندوب',
    date: 'التاريخ',
    from: 'من',
    to: 'إلى',
    minutesUnit: 'دقيقة',
    optionalNote: 'ملاحظة (اختياري)',
    yes: 'نعم',
    no: 'لا',
    exportCsv: 'تصدير CSV',
    exportXlsx: 'تصدير Excel',
    all: 'الكل',
    refresh: 'تحديث',
    metersUnit: 'م',
    note: 'ملاحظة',
    details: 'التفاصيل',
    copied: 'تم النسخ',
    noFileChosen: 'مفيش ملف متختار',
    confirm: 'تأكيد',
    undo: 'تراجع', // New copy (UX audit #2, #26): needs native review.
    // New copy: needs native review.
    myLocation: 'الذهاب إلى موقعي',
    myLocationFailed: 'تعذر تحديد موقعك. اسمح للمتصفح بالوصول إلى الموقع ثم حاول مرة أخرى.',
  },
  // Tangram shell (D119). New Arabic copy: pending native review (P7).
  shell: {
    pin: 'تثبيت القائمة',
    unpin: 'إلغاء تثبيت القائمة',
    menu: 'القائمة',
  },
  nav: {
    overview: 'نظرة عامة',
    dashboard: 'الأداء',
    anomalies: 'مخالفات الموقع',
    syncIssues: 'مشاكل المزامنة',
    configuration: 'الإعدادات',
    catalog: 'الكتالوج',
    administration: 'الإدارة',
    visits: 'الزيارات',
    assignments: 'توزيع الزيارات',
    brands: `${MERCHANT_TERMS.ar.many}`,
    branches: 'الفروع',
    users: 'المستخدمون والصلاحيات',
    devices: 'تنبيهات الأجهزة',
    forms: 'مصمم النماذج',
    import: 'استيراد جماعي',
    travel: 'الوقت بين الزيارات',
    activity: 'الشيفتات والكيلومترات',
    liveMap: 'الخريطة المباشرة',
    route: 'مسار اليوم',
    zoho: 'Zoho Desk',
    geography: 'القطاعات والمناطق',
    locations: 'مواقع الفروع',
    alerts: 'فروع غير مسجلة',
    notifications: 'الإشعارات',
    settings: 'تطبيق الموبايل',
  },
  login: {
    title: 'تسجيل الدخول إلى المحرّك',
    phone: 'رقم الموبايل',
    phoneHint: '01XXXXXXXXX',
    password: 'كلمة المرور',
    submit: 'دخول',
    signingIn: 'جارٍ الدخول…',
  },
  visits: {
    // New copy (UX audit #11): needs native review.
    emptyFiltered: 'لا توجد زيارات تطابق عوامل التصفية.',
    emptyNone: 'لا توجد زيارات في هذه الفترة بعد. تظهر هنا بعد أن يرسلها المندوبون.',
    title: 'الزيارات',
    description:
      'صف لكل زيارة مشروع. الجولة على فرع واحد التي غطت أكثر من مشروع لها صف لكل مشروع، وتتكرر أعمدة الجولة والفرع حتى يمكن تحليل الملف المُصدَّر.',
    statusCount: 'زيارة مشروع في هذه الفترة',
    statusCount_one: 'زيارة مشروع في هذه الفترة',
    statusCount_two: 'زيارتين مشروع في هذه الفترة',
    statusCount_few: 'زيارات مشاريع في هذه الفترة',
    tripRef: `${TRIP_TERMS.ar.trip}`,
    projectOf: 'المشروع {{n}} من {{total}}',
    mainVisit: `${TRIP_TERMS.ar.mainVisit}`,
    additionalVisit: `${TRIP_TERMS.ar.additionalVisit}`,
    project: 'المشروع',
    service: 'الخدمة',
    issue: 'المشكلة',
    photos: 'الصور',
    noPhotos: 'لا توجد صور.',
    fileName: 'اسم الملف على التذكرة',
    ticket: 'التذكرة',
    duration: 'المدة',
    typed: 'غير موجود بالقائمة',
    detailTitle: 'الجولة',
    submissionId: 'رقم الإرسال',
    anomalyScore: 'درجة المخالفة',
    lastError: 'آخر خطأ',
    allAgents: 'كل المندوبين',
    allProjects: 'كل المشاريع',
    allServices: 'كل الخدمات',
    allIssueTypes: 'كل أنواع المشاكل',
    allIssues: 'كل المشاكل',
    anyIssue: 'الكل',
    withIssue: 'بها مشكلة',
    withoutIssue: 'بدون مشكلة',
    position: 'زيارة المشروع',
    projectKey: 'مفتاح المشروع',
    visitType: 'نوع الزيارة',
    serviceDetail: 'تفاصيل الخدمة',
    issueFound: 'يوجد مشكلة',
    issueType: 'نوع المشكلة',
    issues: 'المشاكل',
    comment: 'التعليق',
    filter: {
      projectKey: 'المشروع',
      serviceKey: 'الخدمة',
      issueTypeKey: 'نوع المشكلة',
      issueKey: 'المشكلة',
      issueFound: 'يوجد مشكلة',
    },
  },
  assignments: {
    // New copy (UX audit #26): needs native review.
    showCard: 'عرض',
    title: 'توزيع الزيارات',
    description: 'خطط يوم فريقك: كل مندوب يزور أي فروع، ولأي مشاريع، والمطلوب من الزيارة. المندوب يقبل ويبدأ الزيارة من التطبيق.',
    statusDone: 'من الزيارات الموزعة تمت',
    new: 'توزيع زيارات',
    newTitle: 'توزيع زيارات',
    view: 'العرض',
    planTab: 'خطة اليوم',
    reportTab: 'المتابعة',
    dragHint: 'اسحب الكارت على مندوب تاني لإعادة إسنادها (قبل ما تبدأ). إعادة الإسناد والإلغاء بيتسجلوا في سجل التدقيق.',
    counts: { assigned: 'مُسندة', accepted: 'مقبولة', inProgress: 'جارية', completed: 'تمت', notStarted: 'لم تبدأ', cancelled: 'ملغاة' },
    agent: 'المندوب',
    priority: 'الأولوية',
    date: 'التاريخ',
    time: 'الوقت',
    timeHint: 'اختياري',
    branches: 'الفروع',
    allMerchants: 'كل التجار',
    area: 'المنطقة',
    allAreas: 'كل المناطق',
    searchBranches: 'ابحث عن فرع',
    projects: 'المشاريع',
    projectsHint: 'اختياري. لكل مشروع، نوع الزيارة والخدمة المتوقعين لو معروفين. المندوب يقدر يغيرهم، والاختلاف بيظهر في المتابعة.',
    project: 'المشروع',
    expectedVisitType: 'نوع الزيارة المتوقع',
    expectedService: 'الخدمة المتوقعة',
    notSpecified: 'غير محدد',
    addProject: 'إضافة مشروع',
    removeProject: 'حذف',
    instruction: 'المطلوب من الزيارة',
    // Plural forms (i18next, Intl.PluralRules). New copy: needs native review.
    assignCount_zero: 'إسناد {{count}} فرع',
    assignCount_one: 'إسناد فرع واحد',
    assignCount_two: 'إسناد فرعين',
    assignCount_few: 'إسناد {{count}} فروع',
    assignCount_many: 'إسناد {{count}} فرعًا',
    assignCount_other: 'إسناد {{count}} فرع',
    created_zero: 'لم يتم توزيع أي زيارة',
    created_one: 'تم توزيع زيارة واحدة',
    created_two: 'تم توزيع زيارتين',
    created_few: 'تم توزيع {{count}} زيارات',
    created_many: 'تم توزيع {{count}} زيارة',
    created_other: 'تم توزيع {{count}} زيارة',
    reassign: 'إعادة إسناد',
    // New copy (UX audit #26): needs native review.
    movedTo: 'نُقلت إلى {{name}}',
    movedBack: 'أُعيدت إلى {{name}}',
    // New copy: needs native review.
    edit: 'تعديل',
    editTitle: 'تعديل الزيارة',
    editNotStartedOnly: 'تقدر تعدّل الزيارة طول ما المندوب مبدأهاش. لو غيّرت المندوب، المهمة بترجع مُسندة ولازم يقبلها من جديد.',
    cancel: 'إلغاء المهمة',
    cancelled: 'تم إلغاء المهمة',
    cancelReason: 'سبب الإلغاء',
    noTeam: 'مفيش مندوبين في فريقك. المسؤول بيحدد مدير الفريق لكل مندوب من صفحة المستخدمين.',
    nothingPlanned: 'مفيش زيارات متخططة اليوم ده.',
    reference: 'المرجع',
    acceptedAt: 'وقت القبول',
    startedAt: 'وقت البدء',
    completedAt: 'وقت الإتمام',
    ticket: 'التذكرة',
    deviations: 'الخطة والواقع',
    deviated: 'مختلفة عن الخطة',
    acceptToStart: 'من القبول للبدء',
    startToComplete: 'من البدء للإتمام',
    byAgentDay: 'لكل مندوب في كل يوم',
    deviationsTitle: 'الزيارات اللي اختلفت عن الخطة',
    deviationsHint: 'فرع مختلف، أو مشروع متخطط متزارش، أو مشروع مضاف، أو نوع زيارة أو خدمة مختلفين عن المتوقع.',
    notStartedTitle: 'لم تبدأ حتى {{date}}',
    notStartedHint: 'مُسندة أو مقبولة ومحدش بدأ الزيارة: قائمة المتابعة.',
  },
  assignArea: {
    toggle: 'إسناد منطقة كاملة',
    toggleHint: 'اختار منطقة: كل الفروع الشغالة فيها بتظهر مختارة. شيل الاختيار من اللي مش عايزه، وبعدين أكّد.',
    chooseArea: 'المنطقة',
    loading: 'جارٍ تحميل فروع المنطقة…',
    alreadyAssigned: 'مُسندة بالفعل للمندوب ده في نفس اليوم',
    notLocated: 'مفيش موقع مؤكد',
    selected: '{{count}} من {{total}} فرع مختار',
    truncated: '{{count}} فرع تاني في المنطقة دي مدخلوش في إسناد واحد. قسّم المنطقة أو اسند الباقي لوحده.',
    truncated_one: 'فرع واحد تاني في المنطقة دي مدخلش في إسناد واحد. قسّم المنطقة أو اسند الباقي لوحده.',
    truncated_two: 'فرعين تانيين في المنطقة دي مدخلوش في إسناد واحد. قسّم المنطقة أو اسند الباقي لوحده.',
    truncated_few: '{{count}} فروع تانية في المنطقة دي مدخلوش في إسناد واحد. قسّم المنطقة أو اسند الباقي لوحده.',
  },
  bulk: {
    selectionRegion: 'الصفوف المحددة',
    selected: 'محدد',
    clear: 'إلغاء التحديد',
    delete: 'حذف المحدد',
    confirm: 'حذف {{count}}',
    listSeparator: '، ',
    andMore: '{{names}} و {{count}} غيرهم',
    unknownRow: 'صف غير معروف',
    willSkip: '{{count}} هيتم تخطيهم:',
    nothingToDo: 'مفيش ولا صف من المحدد ينفع يتحذف.',
    resultTitle: 'انتهى الحذف الجماعي',
    resultSummary: 'اتحذف: {{deleted}} · اتوقف: {{deactivated}} · اتخطى: {{skipped}} · فشل: {{failed}}',
    doneToast: 'اتحذف {{deleted}}، واتوقف {{deactivated}}',
    allDone: 'كل الصفوف المحددة اتنفذت.',
    outcome: { DELETED: 'اتحذف', DEACTIVATED: 'اتوقف', SKIPPED: 'اتخطى', FAILED: 'فشل' },
    reason: {
      HAS_HISTORY: 'ليه زيارات، فهيتوقف بدل ما يتحذف',
      ACCOUNT_REMOVED: 'اتشال واتعمله تسجيل خروج، وزياراته محفوظة',
      NOT_FOUND: 'مش موجود، أو اتحذف قبل كده',
      OWN_ACCOUNT: 'مينفعش تحذف حسابك',
      SUPER_ADMIN_ONLY: 'المسؤول الأعلى بس هو اللي يقدر يحذف مسؤول أعلى',
      LAST_SUPER_ADMIN: 'مينفعش تحذف آخر مسؤول أعلى نشط',
      UNEXPECTED: 'خطأ غير متوقع',
    },
    brands: {
      confirmTitle: `حذف {{count}} من ${MERCHANT_TERMS.ar.many}؟`,
      confirmTitle_one: `حذف ${MERCHANT_TERMS.ar.one} واحد؟`,
      confirmTitle_two: 'حذف تاجرين؟',
      confirmTitle_few: `حذف {{count}} ${MERCHANT_TERMS.ar.manyIndefinite}؟`,
      willDelete: '{{count}} ملهمش زيارات هيتحذفوا نهائياً، هما وفروعهم:',
      willDelete_one: 'واحد ملوش زيارات هيتحذف نهائياً، هو وفروعه:',
      willDelete_two: '{{count}} ملهمش زيارات هيتحذفوا نهائياً، هما وفروعهم:',
      willDelete_few: '{{count}} ملهمش زيارات هيتحذفوا نهائياً، هما وفروعهم:',
      willDeactivate: '{{count}} ليهم زيارات هيتوقفوا بدل الحذف، عشان زياراتهم تفضل محفوظة:',
      willDeactivate_one: 'واحد ليه زيارات هيتوقف بدل الحذف، عشان زياراته تفضل محفوظة:',
      willDeactivate_two: '{{count}} ليهم زيارات هيتوقفوا بدل الحذف، عشان زياراتهم تفضل محفوظة:',
      willDeactivate_few: '{{count}} ليهم زيارات هيتوقفوا بدل الحذف، عشان زياراتهم تفضل محفوظة:',
      branchesAffected: '{{count}} فرع هيتحذف أو يتوقف معاهم.',
      branchesAffected_one: 'فرع واحد هيتحذف أو يتوقف معاهم.',
      branchesAffected_two: 'فرعين هيتحذفوا أو يتوقفوا معاهم.',
      branchesAffected_few: '{{count}} فروع هيتحذفوا أو يتوقفوا معاهم.',
    },
    branches: {
      confirmTitle: 'حذف {{count}} فرع؟',
      confirmTitle_one: 'حذف فرع واحد؟',
      confirmTitle_two: 'حذف فرعين؟',
      confirmTitle_few: 'حذف {{count}} فروع؟',
      willDelete: '{{count}} ملهمش زيارات هيتحذفوا نهائياً:',
      willDelete_one: 'واحد ملوش زيارات هيتحذف نهائياً:',
      willDelete_two: '{{count}} ملهمش زيارات هيتحذفوا نهائياً:',
      willDelete_few: '{{count}} ملهمش زيارات هيتحذفوا نهائياً:',
      willDeactivate: '{{count}} ليهم زيارات هيتوقفوا بدل الحذف، عشان زياراتهم تفضل محفوظة:',
      willDeactivate_one: 'واحد ليه زيارات هيتوقف بدل الحذف، عشان زياراته تفضل محفوظة:',
      willDeactivate_two: '{{count}} ليهم زيارات هيتوقفوا بدل الحذف، عشان زياراتهم تفضل محفوظة:',
      willDeactivate_few: '{{count}} ليهم زيارات هيتوقفوا بدل الحذف، عشان زياراتهم تفضل محفوظة:',
    },
    users: {
      confirmTitle: 'حذف {{count}} مستخدم؟',
      confirmTitle_one: 'حذف مستخدم واحد؟',
      confirmTitle_two: 'حذف مستخدمين اتنين؟',
      confirmTitle_few: 'حذف {{count}} مستخدمين؟',
      willDelete: '{{count}} هيتشالوا ويتعملهم تسجيل خروج. زياراتهم تفضل محفوظة:',
      willDelete_one: 'واحد هيتشال ويتعمله تسجيل خروج. زياراته تفضل محفوظة:',
      willDelete_two: '{{count}} هيتشالوا ويتعملهم تسجيل خروج. زياراتهم تفضل محفوظة:',
      willDelete_few: '{{count}} هيتشالوا ويتعملهم تسجيل خروج. زياراتهم تفضل محفوظة:',
      willDeactivate: '{{count}} هيتوقفوا:',
      willDeactivate_one: 'واحد هيتوقف:',
      willDeactivate_two: '{{count}} هيتوقفوا:',
      willDeactivate_few: '{{count}} هيتوقفوا:',
    },
  },
  users: {
    // New copy (UX audit #11): needs native review.
    emptyFiltered: 'لا يوجد مستخدمون يطابقون عوامل التصفية.',
    emptyNone: 'لا يوجد مستخدمون بعد.',
    title: 'المستخدمون والصلاحيات',
    description: 'حسابات المحرّك والتطبيق الميداني، مع صلاحيات الدور والتعديلات لكل مستخدم.',
    statusCount: 'مستخدم',
    statusCount_one: 'مستخدم',
    statusCount_two: 'مستخدمين',
    statusCount_few: 'مستخدمين',
    newUser: 'مستخدم جديد',
    fullName: 'الاسم الكامل',
    phone: 'رقم الموبايل',
    role: 'الدور',
    allRoles: 'كل الأدوار',
    allStates: 'كل الحالات',
    password: 'كلمة المرور',
    newPassword: 'كلمة مرور جديدة (اتركها فارغة للإبقاء)',
    passwordPolicy: '10 أحرف على الأقل تشمل حروفاً وأرقاماً.',
    lastLogin: 'آخر دخول',
    permissions: 'الصلاحيات الفعلية',
    permissionColumn: 'الصلاحية',
    overrides: 'تعديل الصلاحيات',
    manager: 'مدير الفريق',
    managerHint: 'يخطط زيارات هذا المستخدم (بصلاحية «توزيع الزيارات»).',
    noManager: 'بدون فريق',
    roleDefault: 'حسب الدور',
    override: 'التعديل',
    effective: 'الفعلية',
    inherit: 'يتبع الدور',
    grant: 'ممنوحة',
    revoke: 'مسحوبة',
    overrideCount_zero: 'بدون تعديلات',
    overrideCount_one: '+{{count}} تعديل',
    overrideCount_two: '+{{count}} تعديلان',
    overrideCount_few: '+{{count}} تعديلات',
    overrideCount_many: '+{{count}} تعديلًا',
    overrideCount_other: '+{{count}} تعديل',
    deactivate: 'تعطيل',
    activate: 'تفعيل',
    deleted: 'تم حذف المستخدم (محفوظ في السجل)',
    // New copy (UX audit #2, #26): needs native review.
    deactivateTitle: 'تعطيل {{name}}؟',
    deactivateConsequence: 'سيُسجَّل خروجه من المحرّك ومن تطبيق المندوب فورًا، ولن يستطيع الدخول حتى يُعاد تفعيل الحساب.',
    deactivatedToast: 'تم تعطيل {{name}}',
    reactivatedToast: 'تم تفعيل {{name}} مرة أخرى',
    deleteTitle: 'حذف {{name}}؟',
    deleteConsequence: 'سيُحذف الحساب ويُسجَّل خروجه من كل الأجهزة، ويبقى سجل زياراته محفوظًا. لا يمكن التراجع عن ذلك.',
    searchPlaceholder: 'الاسم أو رقم الموبايل',
    roles: {
      SUPER_ADMIN: 'مدير النظام',
      OPERATIONS_MANAGER: 'مدير العمليات والكتالوج',
      FORM_DESIGNER: 'مصمم النماذج',
      SUPERVISOR: 'مشرف ميداني',
      FIELD_AGENT: 'مندوب ميداني',
    },
    permission: {
      manageBrands: `إدارة ${MERCHANT_TERMS.ar.many}`,
      manageBranches: 'إدارة الفروع والمواقع',
      setGeofenceRadius: 'تحديد نطاق الفرع',
      bulkImportCatalog: 'الاستيراد الجماعي',
      reviewUnlistedBranches: 'مراجعة الفروع غير المسجلة',
      manageForms: 'تصميم النماذج',
      manageUsers: 'إدارة المستخدمين',
      viewAnalytics: 'عرض الأداء',
      viewAnomalies: 'عرض مخالفات الموقع',
      assignVisits: 'توزيع الزيارات على فريقه',
      manageZoho: 'إعداد Zoho',
      manageAppDistribution: 'إدارة توزيع التطبيق',
      mobileCheckin: 'استخدام التطبيق الميداني',
      viewAgentLocation: 'عرض موقع المندوب',
      sendNotifications: 'إرسال الإشعارات',
    },
  },
  devices: {
    title: 'تنبيهات الأجهزة الجديدة',
    description:
      'دخول من موبايل غير اللي الحساب مربوط بيه. رقم الجهاز التطبيق هو اللي بيعمله، مش بيتقري من الجهاز: لو التطبيق اتشال واتنزل تاني أو الموبايل اتعمله فورمات بيطلع رقم جديد، فممكن مندوب سليم يعمل التنبيه ده. وهو في الانتظار، المندوب بيشتغل عادي.',
    devicesOf: 'موبايلات {{name}}',
    manage: 'الموبايلات',
    requireApproval: 'الموبايل الجديد محتاج موافقة',
    requireApprovalHint: 'مفعّل: الموبايل الجديد بيفضل في الانتظار لحد الموافقة (وبيشتغل). مقفول: بيتسمح بيه على طول، والتنبيه برضه بيظهر.',
    approve: 'موافقة',
    reject: 'رفض',
    dismiss: 'إغلاق التنبيه',
    rejectConfirm: 'الرفض بيخرّج الموبايل ده وبيمنعه على الحساب ده لحد ما يتوافق عليه. تكمّل؟',
    rejectNote: 'السبب (اختياري)',
    first: 'أول موبايل',
    model: 'الموبايل',
    appVersion: 'التطبيق',
    firstSeen: 'أول ظهور',
    lastSeen: 'آخر ظهور',
    deviceId: 'رقم الجهاز',
    statuses: { APPROVED: 'موافق عليه', PENDING: 'في الانتظار', REJECTED: 'مرفوض' },
    open: 'مفتوحة',
    resolved: 'اتحلّت',
    noAlerts: 'مفيش تنبيهات مفتوحة.',
  },
  errors: {
    NETWORK_ERROR: 'تعذر الاتصال بالخادم من هذا العنوان. تحقق من الاتصال ومن إعدادات الخادم.',
    ORIGIN_NOT_ALLOWED: 'تعذر الاتصال بالخادم من هذا العنوان. راجع إعدادات الخادم.',
    UNAUTHORIZED: 'انتهت الجلسة. سجّل الدخول مرة أخرى.',
    FORBIDDEN: 'هذا الحساب ليس له صلاحية لتنفيذ هذا الإجراء.',
    NOT_FOUND: 'غير موجود.',
    CONFLICT: 'يتعارض مع بيانات موجودة.',
    RATE_LIMITED: 'محاولات كثيرة. انتظر ثم حاول مرة أخرى.',
    VALIDATION_ERROR: 'في بيانات مش مقبولة. راجعها وحاول تاني.', // new — needs native review
    checkFields: 'راجع الحقول المحددة.',
    PAYLOAD_TOO_LARGE: 'الملف أو البيانات أكبر من المسموح.',
    UNSUPPORTED_MEDIA_TYPE: 'نوع الملف ده مش مقبول.',
    PRECONDITION_FAILED: 'البيانات اتغيرت في الوقت ده. حدّث الصفحة وحاول تاني.',
    ZOHO_NOT_CONFIGURED: 'لم يتم إعداد Zoho Desk.',
    ZOHO_ERROR: 'أرجع Zoho Desk خطأ.',
    INTERNAL_ERROR: 'خطأ غير متوقع في الخادم.',
    unknown: 'حصل خطأ غير متوقع. كود الخطأ: {{code}}',
    codeLabel: 'كود الخطأ:',
    reason: {
      INVALID_CREDENTIALS: 'رقم الموبايل أو كلمة المرور غلط.',
      ACCOUNT_INACTIVE: 'الحساب ده متوقف. تواصل مع المسؤول.',
      NO_ENGINE_ACCESS: 'الحساب ده مينفعش يدخل لوحة الإدارة.',
      TOKEN_EXPIRED: 'انتهت الجلسة. سجّل الدخول مرة أخرى.',
      TOKEN_INVALID: 'انتهت الجلسة. سجّل الدخول مرة أخرى.',
      SESSION_REVOKED: 'الجلسة دي اتقفلت. سجّل الدخول مرة أخرى.',
      // User management (USER_ADMIN_REASONS); the permission reasons list {{permissions}}.
      SUPER_ADMIN_ONLY_ASSIGNS_SUPER_ADMIN: 'مدير النظام بس هو اللي يقدر يدّي دور مدير النظام.', // new — needs native review
      CANNOT_GRANT_UNHELD_PERMISSIONS: 'مينفعش تدّي صلاحيات إنت نفسك معندكش: {{permissions}}.', // new — needs native review
      EXCEEDS_OWN_PERMISSIONS: 'الدور ده مع التعديلات هيدّي صلاحيات أكتر من صلاحياتك: {{permissions}}.', // new — needs native review
      CANNOT_DEACTIVATE_SELF: 'مينفعش توقف حسابك بنفسك.', // new — needs native review
      CANNOT_EDIT_SELF_ROLE: 'مينفعش تغيّر دورك أو صلاحياتك بنفسك.', // new — needs native review
      SUPER_ADMIN_ONLY_MODIFIES_SUPER_ADMIN: 'مدير النظام بس هو اللي يقدر يعدّل حساب مدير نظام.', // new — needs native review
      CANNOT_DELETE_SELF: 'مينفعش تحذف حسابك.', // new — needs native review
      SUPER_ADMIN_ONLY_DELETES_SUPER_ADMIN: 'مدير النظام بس هو اللي يقدر يحذف مدير نظام.', // new — needs native review
      LAST_SUPER_ADMIN: 'ده آخر مدير نظام نشط: مينفعش يتغيّر دوره أو يتوقف أو يتحذف.', // new — needs native review
      OWN_MANAGER: 'المستخدم مينفعش يكون مدير فريق نفسه.', // new — needs native review
      MANAGER_LOOP: 'المدير ده من فريق المستخدم نفسه. اختار حد تاني.', // new — needs native review
      PHONE_IN_USE: 'رقم الموبايل ده مستخدم في حساب تاني.', // new — needs native review
      PHONE_OF_DELETED_USER: 'رقم الموبايل ده بتاع حساب محذوف.', // new — needs native review
      // APK upload and publish (APK_UPLOAD_REASONS).
      APK_NOT_MULTIPART: 'الملف ماتبعتش كرفع ملف. جرّب تاني من الصفحة دي.', // new — needs native review
      APK_FILE_MISSING: 'مفيش ملف APK وصل. اختار الملف وجرّب تاني.', // new — needs native review
      APK_TRUNCATED: 'الملف صغير جداً إنه يكون APK.', // new — needs native review
      APK_NOT_A_ZIP: 'الملف ده مش APK.', // new — needs native review
      APK_NO_CENTRAL_DIRECTORY: 'الـ APK ناقص أو بايظ. ارفعه تاني.', // new — needs native review
      APK_NO_ANDROID_MANIFEST: 'ده مش تطبيق أندرويد: الملف مفيهوش AndroidManifest.xml.', // new — needs native review
      APK_TOO_LARGE: 'الـ APK أكبر من الحد المسموح ({{maxMb}}).', // new — needs native review
      APK_DEV_BUILD_NOT_PUBLISHABLE: 'دي نسخة تطوير (محتاجة Metro) ومينفعش تتنشر. ارفع نسخة release.', // new — needs native review
      // Zoho (ZOHO_FAILURE_REASONS): the test ticket's error, the connection test, the last test, alerts.
      ZOHO_NOT_CONFIGURED: 'Zoho Desk مش متظبط بالكامل: راجع بيانات الدخول والاتصال ورقم المؤسسة والقسم.', // new — needs native review
      ZOHO_TIMEOUT: 'Zoho Desk مردّش في الوقت المحدد. جرّب تاني.', // new — needs native review
      ZOHO_NETWORK: 'السيرفر مش قادر يوصل لـ Zoho Desk. راجع اتصال السيرفر.', // new — needs native review
      ZOHO_AUTH_REJECTED: 'Zoho رفض بيانات الاتصال. اعمل إعادة اتصال بـ Zoho.', // new — needs native review
      ZOHO_ACCESS_DENIED: 'مستخدم Zoho المتصل ملوش صلاحية على المؤسسة أو القسم ده.', // new — needs native review
      ZOHO_NOT_FOUND: 'Zoho ملقاش المؤسسة أو القسم أو التذكرة أو جهة الاتصال.', // new — needs native review
      ZOHO_RATE_LIMITED: 'طلبات كتير على Zoho. استنى شوية وجرّب تاني.', // new — needs native review
      ZOHO_UNAVAILABLE: 'Zoho Desk مش متاح دلوقتي. جرّب تاني بعدين.', // new — needs native review
      ZOHO_REQUEST_REJECTED: 'Zoho رفض الطلب، مثلاً قيمة حقل مش مقبولة عنده.', // new — needs native review
      ZOHO_INVALID_RESPONSE: 'Zoho رجّع رد مش متوقع.', // new — needs native review
      ZOHO_MAPPINGS_INVALID: 'ربط الحقول الحالي مينفعش يعمل تذكرة. راجعه في مصمم النماذج.', // new — needs native review
      ZOHO_BRANCH_NOT_MAPPED: 'نموذج الزيارة مفيهوش حقل Zoho مربوط بالفرع.', // new — needs native review
      ZOHO_UNEXPECTED: 'خطأ غير متوقع مع Zoho.', // new — needs native review
    },
    issue: {
      default: 'القيمة دي مش صحيحة.',
      invalid_type: 'مطلوب، أو نوع القيمة غلط.',
      too_small: 'أقصر أو أصغر من المسموح.',
      too_big: 'أطول أو أكبر من المسموح.',
      invalid_format: 'الصيغة مش صحيحة.',
      invalid_value: 'القيمة دي مش من الاختيارات المسموحة.',
      not_multiple_of: 'الرقم ده مش مسموح.',
      unrecognized_keys: 'فيه حقول مش متوقعة.',
      invalid_union: 'القيمة دي مش صحيحة.',
      custom: 'القيمة دي مش صحيحة.',
    },
  },
  ...formsAr,
  ...importAr,
  ...analyticsAr,
  ...trackingAr,
  ...catalogAr,
  ...adminAr,
};
